import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, ArrowRight, Target, Code, Cloud, Database, Shield, Lock, Bot, Sparkles, Eye, Mic, TrendingUp, Scale, GraduationCap, Heart, Blocks, Utensils } from 'lucide-react';
import { ServiceCard } from '../components/ServiceCard';
import { VideoBackground } from '../components/VideoBackground';
import { StructuredData } from '../components/StructuredData';
import { Schema } from '../components/Schema';
import { Service } from '../types';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const HomePage: React.FC = () => {
  useSEO('home');
  const { isBannerVisible, bannerHeight } = useBanner();
  
  const services: Service[] = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Cognitive Reach",
      subtitle: "Digital Marketing",
      description:
        "AI-powered marketing solutions that amplify your brand's reach and engagement across digital channels.",
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Cognitive Train",
      subtitle: "AI Training & Consulting",
      description:
        "Comprehensive AI education, custom model training, strategy consulting, and change management for successful AI adoption.",
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Cognitive Cloud",
      subtitle: "Cloud Infrastructure",
      description:
        "Scalable, secure cloud solutions that grow with your business and provide reliable infrastructure.",
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Cognitive Build",
      subtitle: "Custom Software",
      description:
        "Tailored software solutions built with cutting-edge technologies to solve your unique business challenges.",
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Cognitive Intelligence",
      subtitle: "AI & Data Tools",
      description:
        "Advanced AI and data analytics tools that transform raw data into actionable business insights.",
    },
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Cognitive Agents",
      subtitle: "AI Automation & RPA",
      description:
        "Intelligent automation solutions with AI agents, robotic process automation, and workflow optimization.",
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Cognitive Generate",
      subtitle: "Generative AI Solutions",
      description:
        "Custom generative AI implementations including content creation, code generation, and multimodal AI solutions.",
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Cognitive Vision",
      subtitle: "Computer Vision & AI Analytics",
      description:
        "Advanced computer vision solutions for image and video analytics, quality control automation, and visual data processing.",
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Cognitive Voice",
      subtitle: "Conversational AI & Voice",
      description:
        "AI-powered voice solutions including chatbots, virtual assistants, speech analytics, and interactive voice response systems.",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Cognitive Predict",
      subtitle: "Advanced Analytics & ML",
      description:
        "Machine learning-powered predictive analytics for demand forecasting, risk assessment, and data-driven decision making.",
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Cognitive IoT",
      subtitle: "Internet of Things",
      description:
        "Connected device solutions that bridge the physical and digital worlds for smarter operations.",
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Cognitive Secure",
      subtitle: "Cybersecurity",
      description:
        "Advanced cybersecurity solutions powered by AI to protect your business from evolving cyber threats.",
    },
    {
      icon: <Utensils className="w-8 h-8" />,
      title: "Cognitive Robotics",
      subtitle: "Robotics Implementation",
      description:
        "End-to-end robotics automation consulting and implementation. We handle vendor selection, system integration, and full support for your robotics deployment.",
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Cognitive Compliance",
      subtitle: "Regulatory & Legal AI",
      description:
        "Specialized AI solutions for regulatory compliance, legal document analysis, audit automation, and governance frameworks.",
    },
    {
      icon: <Blocks className="w-8 h-8" />,
      title: "Cognitive Ledger",
      subtitle: "Blockchain & Distributed Ledger",
      description:
        "Secure blockchain solutions including smart contracts, DApps, supply chain transparency, and decentralized identity management.",
    },
  ];

  return (
    <>
      <StructuredData type="organization" />
      <StructuredData type="service" />
      <Schema type="organization" />
      <Schema type="localBusiness" />
      <main className="pb-20">
      {/* Hero Section - Special handling for full viewport */}
      <section 
        className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8"
        style={{ 
          marginTop: isBannerVisible ? `${bannerHeight + 64}px` : '64px',
          minHeight: isBannerVisible ? `calc(100vh - ${bannerHeight + 64}px)` : 'calc(100vh - 64px)'
        }}
      >
        {/* Video Background - only for hero section */}
        <VideoBackground 
          src="https://customer-d2f4103s9hv71t3d.cloudflarestream.com/7fb69a504162eb875fe4710d80ae9ca8/manifest/video.m3u8"
          fallbackImage="/images/hero-fallback.jpg"
        />

        {/* Fallback animated background - behind video */}
        <div className="absolute inset-0 overflow-hidden opacity-30 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-600/10 animate-pulse" />
          <div
            className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-bounce"
            style={{ animationDelay: "0s", animationDuration: "6s" }}
          />
          <div
            className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-bounce"
            style={{ animationDelay: "2s", animationDuration: "8s" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto text-center z-20 my-8 sm:my-12 md:my-16">
          {/* Semi-transparent backdrop for text */}
          <div className="bg-white/10 dark:bg-black/20 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <h1 
              className="text-5xl md:text-7xl font-bold mb-8 py-4 px-2 text-gray-800 dark:text-gray-100 leading-loose overflow-visible min-h-fit"
              style={{ lineHeight: '1.75', minHeight: 'auto', overflow: 'visible' }}
            >
              Cognitive Craft Hub
            </h1>

            <p className="text-xl md:text-2xl mb-8 text-gray-700 dark:text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Leading AI solutions company with specialized artificial intelligence services. 
              From AI automation and machine learning to generative AI and computer vision - 
              empowering enterprises with comprehensive AI solutions for digital transformation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/about"
                className="px-8 py-4 border-2 border-gray-600 dark:border-gray-400 text-gray-800 dark:text-gray-200 font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="services-section" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:bg-gradient-to-r dark:from-blue-400 dark:to-blue-400 dark:bg-clip-text dark:text-transparent">
              Our AI-Powered Services
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-7xl mx-auto">
              Complete AI solutions ecosystem with specialized artificial intelligence services. 
              AI automation, machine learning, generative AI, computer vision, predictive analytics, and more - 
              designed to revolutionize business operations with cutting-edge AI technology.
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>

          {/* View All Services CTA */}
          <div className="text-center mt-16">
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Explore All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* AI Solutions Highlight Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50 dark:bg-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:bg-gradient-to-r dark:from-blue-400 dark:to-blue-400 dark:bg-clip-text dark:text-transparent">
              Leading AI Innovation
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto">
              From AI automation and generative AI to computer vision and predictive analytics, 
              we deliver comprehensive AI solutions that position your business at the forefront of artificial intelligence innovation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-6 flex items-center justify-center">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">AI Automation</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Transform operations with intelligent agents, RPA, and workflow automation that significantly boost productivity
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-6 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Generative AI</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Harness the power of GPT and generative AI for content creation, code generation, and creative solutions
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-6 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Predictive Intelligence</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Make data-driven decisions with advanced analytics, forecasting, and machine learning models
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};